import { useMemo } from 'react'
import { FC } from 'react'
import * as styles from 'components/features/socialFeed/FeedAuthorPage/FeedAuthorPage.styl'
import { FeedAvatarWithInfo } from 'components/features/socialFeed/FeedAvatarWithInfo/FeedAvatarWithInfo'
import { FeedUserBadgesExtended } from 'components/features/socialFeed/FeedUserInfo/FeedUserBadgesExtended'
import { PageHeading } from 'components/atoms/Text'
import { FeedExternalLayout, FeedInternalLayout } from 'components/layouts/Layouts'
import FeedPostListContainer from 'components/features/socialFeed/FeedPostList/FeedPostListContainer'
import { Anchor as AnchorType, AnchorPlacement } from 'interfaces/navigation'
import PageLoadingOverlay from 'components/atoms/preloaders/PageLoadingOverlay'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { SimpleGuard } from 'interfaces/guards'
import { createIsCurrentlySelectedAuthorLoaded, isFeedUserMemberSelector, createFeedAuthorSelector } from 'selectors/feedSelectors'
import { getFeedAuthorActionCreator } from 'actions/feedActions'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { UserAvatar } from 'components/features/socialFeed/Avatar/UserAvatar'
import { FeedUserResource } from 'interfaces/feed'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { FeedUserCounters } from 'components/features/socialFeed/FeedUserCounters/FeedUserCounters'
import { isMobileSelector, viewportSegmentSelector } from 'selectors/viewportSelectors'
import { ResourceFollowButton } from 'components/features/pfm/ResourceFollowButton/ResourceFollowButton'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'

export type FeedAuthorPageInnerProps = FeedUserResource

const feedAuthorGuard: SimpleGuard<FeedAuthorPageInnerProps> = {
  name: `Feed Page Author`,
  checker: createIsCurrentlySelectedAuthorLoaded(),
  loader: (dispatch, ownProps) => {
    const { name } = ownProps
    dispatch(getFeedAuthorActionCreator.request({ username: name }))
  },
}

export const FeedAuthorPageInner: FC<FeedAuthorPageInnerProps> = (props) => {
  const { name } = props

  const feedAuthorSelector = useMemo(createFeedAuthorSelector, [])
  const isMobile = useStoreSelector(isMobileSelector)
  const author = useStoreSelector((state) => feedAuthorSelector(state, props))
  const isSelf = useStoreSelector(
    (state) => author
      ? !!isFeedUserMemberSelector(state, { userUid: author.uid })
      : false
  )
  const authorName = author ? author.username : name
  const isAuthenticated = useStoreSelector(isAuthenticatedSelector)

  const viewportSegment = useStoreSelector(viewportSegmentSelector)
  const followButtonMode = useMemo(() => {
    if (viewportSegment === 'palm-mobile') {
      return 'large'
    }

    if (viewportSegment === 'mobile') {
      return 'medium'
    }

    return 'small'
  }, [viewportSegment])

  const isFeedAuthorLoaded = useSimpleGuard(feedAuthorGuard, props, 'FeedAuthorPage')

  const authorAnchor: AnchorType = useMemo(() => {
    const anchorPlacement: AnchorPlacement = 'feedauthor'
    return {
      anchor: anchorPlacement,
      id: authorName,
    }
  }, [authorName])

  const contentChild = useMemo(() => {
    if (!isFeedAuthorLoaded || !author) {
      return (
        <PageLoadingOverlay />
      )
    }
    return (
      <>
        <PageMeta
          title={ `${ author.username } posts on Status Money` }
          description={ `See the latest posts from ${ author.username } on Status Money. Join the conversation.` }
        />
        <section className={ styles.column }>
          <header className={ styles.header }>
            <div className={ styles.avatar }>
              {
                author.peergroup && author.peergroup.match
                  ? (
                    <FeedAvatarWithInfo
                      userUid={ author.uid }
                      username={ author.username }
                      peergroup={ author.peergroup }
                      mode='page'
                    />
                  )
                  : <UserAvatar userUid={ author.uid } matchRatio={ null } />
              }
            </div>
            <div className={ styles.resourceName }>
              <PageHeading className={ styles.pageHeading }>{author.username}</PageHeading>
              <div className={ styles.countersWrapper }>
                <FeedUserCounters
                  followersCount={ author.followers_count }
                  likesCount={ author.likes_count }
                  postsCount={ author.posts_count }
                  mode={ isMobile ? 'sparse' : 'compact' }
                />
                {isMobile && <FeedUserBadgesExtended user={ author } />}
              </div>
            </div>
            <div className={ styles.followButtonWrapper }>
              {!isSelf && (
                <ResourceFollowButton
                  { ...props }
                  mode={ followButtonMode }
                />
              )}
            </div>
          </header>
          <FeedPostListContainer
            resource={{
              type: 'user',
              name: author.username,
            }}
          />
        </section>
      </>
    )
  }, [author, followButtonMode, isFeedAuthorLoaded, isMobile, isSelf, props])

  if (isAuthenticated) {
    return (
      <FeedInternalLayout anchor={ authorAnchor } >
        { contentChild}
      </FeedInternalLayout >
    )
  }

  return (
    <FeedExternalLayout>
      { contentChild}
    </FeedExternalLayout>
  )
}
